import React, { Component } from "react";
import { Configuration, getDataTable } from "../Configuration";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { Modal, Spin } from "antd";
import "antd/dist/antd.css";

const COUNTRIES = ["UAE", "KSA"];
const ENVIRONMENTS = ["PRODUCTION", "STAGING"];

/* Portals that this page can store credentials for. Shared with the user-level
   frmNamshiCredentials page; adding a new value here plus a push handler on
   the backend is the entire change needed to onboard another marketplace. */
const PORTALS = ["NAMSHI", "SHOPIFY"];
const DEFAULT_URL = {
  PRODUCTION: "https://marketplace.namshi.net",
  STAGING: "https://marketplace.namstg.net",
};

/* Same reasoning as the org-scoped page: these values are concatenated into an
   EXEC string, and a credential JSON is full of quotes. Double every single
   quote so a stray apostrophe cannot close the literal. */
const q = (v) =>
  v === null || v === undefined ? "" : String(v).replace(/'/g, "''");

/* Administrator view of Namshi credentials — every organisation, not just the
   caller's own. Reaching this screen is gated by AdminRoute, but that is only
   presentation: NamshiCredAdmin_* re-checks the same admin list in SQL, so a
   non-admin who calls the procs directly is refused rather than served. */
export default class frmNamshiCredentialsAdmin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loginId: Configuration.USER_ID,
      loading: false,
      denied: false,

      orgs: [],
      brands: [],
      rows: [],

      filterOrg: "0",
      filterPortal: "",
      search: "",

      // editor
      brandID: "",
      portal: "NAMSHI",
      country: "UAE",
      environment: "PRODUCTION",
      projectName: "",
      projectID: "",
      credToken: "",
      apiUrl: DEFAULT_URL.PRODUCTION,
      jsonText: null,
      jsonFileName: "",
      editingCredID: null,
      tokenMask: "",        // listing shows only a mask, e.g. ••••••••0054
      hasStoredToken: false,
    };
    this.fileRef = React.createRef();
  }

  async componentDidMount() {
    await this.loadOrgs();
    await this.loadBrands();
    await this.loadRows();
  }

  // ── Loads ─────────────────────────────────────────────────────────────────

  loadOrgs = async () => {
    const dt = await getDataTable(
      "EXEC dbo.NamshiCredAdmin_GetOrgs @loginID = " + this.state.loginId
    );
    // The procs RAISERROR for a non-admin, and getDataTable turns that into
    // false. Show a clear refusal rather than an empty screen.
    if (dt === false) {
      this.setState({ denied: true });
      return;
    }
    this.setState({ orgs: Array.isArray(dt) ? dt : [] });
  };

  loadBrands = async () => {
    const dt = await getDataTable(
      "EXEC dbo.NamshiCredAdmin_GetBrands @loginID = " + this.state.loginId
    );
    this.setState({ brands: Array.isArray(dt) ? dt : [] });
  };

  loadRows = async () => {
    const { loginId, filterOrg, filterPortal, search } = this.state;
    this.setState({ loading: true });
    const dt = await getDataTable(
      "EXEC dbo.NamshiCredAdmin_ListAll @loginID = " + loginId +
      ", @orgID = " + (parseInt(filterOrg, 10) || 0) +
      ", @search = '" + q(search) + "'" +
      ", @portal = '" + q(filterPortal || "") + "'"
    );
    this.setState({ rows: Array.isArray(dt) ? dt : [], loading: false });
  };

  // ── Editor ────────────────────────────────────────────────────────────────

  onEnvChange = (env) => {
    const isDefault =
      !this.state.apiUrl ||
      Object.values(DEFAULT_URL).indexOf(this.state.apiUrl) !== -1;
    this.setState({
      environment: env,
      apiUrl: isDefault ? DEFAULT_URL[env] : this.state.apiUrl,
    });
  };

  onPickJson = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target.result || "");
      try {
        JSON.parse(text);
      } catch (err) {
        Swal.fire("Invalid JSON", "That file is not valid JSON.", "error");
        if (this.fileRef.current) this.fileRef.current.value = "";
        return;
      }
      this.setState({ jsonText: text, jsonFileName: f.name });
    };
    reader.readAsText(f);
  };

  resetEditor = () => {
    this.setState({
      brandID: "", portal: "NAMSHI",
      country: "UAE", environment: "PRODUCTION",
      projectName: "", projectID: "", credToken: "",
      apiUrl: DEFAULT_URL.PRODUCTION,
      jsonText: null, jsonFileName: "", editingCredID: null,
      tokenMask: "", hasStoredToken: false,
    });
    if (this.fileRef.current) this.fileRef.current.value = "";
  };

  editRow = (r) => {
    this.setState({
      brandID: String(r.BrandID),
      country: r.Country,
      environment: r.Environment,
      projectName: r.ProjectName || "",
      projectID: r.ProjectID || "",
      credToken: "",
      tokenMask: r.CredTokenMask || "",
      hasStoredToken: !!r.hasToken,
      apiUrl: r.ApiUrl || DEFAULT_URL[r.Environment] || "",
      jsonText: null,
      jsonFileName: r.JsonFileName || "",
      editingCredID: r.CredID,
      // Keep the portal of the row being edited rather than snapping back to
      // NAMSHI, so clicking Edit then Update does not rewrite a Shopify row
      // into a Namshi one.
      portal: r.Portal || "NAMSHI",
    });
    if (this.fileRef.current) this.fileRef.current.value = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  save = async () => {
    const {
      loginId, brandID, country, environment,
      projectName, projectID, credToken, apiUrl, jsonText, jsonFileName, brands,
    } = this.state;

    if (!brandID) {
      Swal.fire({ icon: "info", title: "Please select a Brand", timer: 1800, showConfirmButton: false });
      return;
    }
    // Required only for a NEW credential. Blank on edit means "keep stored".
    if ((!credToken || !credToken.trim()) && !this.state.hasStoredToken) {
      Swal.fire({ icon: "info", title: "Please enter the Credential Token", timer: 1800, showConfirmButton: false });
      return;
    }

    // An admin edits other people's organisations, so name the target before
    // writing a production credential.
    const brand = brands.find((b) => String(b.DisplayId) === String(brandID));
    const confirm = await Swal.fire({
      title: this.state.editingCredID ? "Update this credential?" : "Save this credential?",
      html:
        "<div style='text-align:left;font-size:13px'>" +
        "<b>Brand:</b> " + (brand ? brand.DisplayName : brandID) + "<br/>" +
        "<b>Organisation:</b> " + (brand ? brand.OrgName : "—") + "<br/>" +
        "<b>Country:</b> " + country + " &nbsp; <b>Environment:</b> " + environment +
        (environment === "PRODUCTION"
          ? "<br/><br/><span style='color:#cf1322'>This is a PRODUCTION credential — it will be used for live pushes.</span>"
          : "") +
        "</div>",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.state.editingCredID ? "Update" : "Save",
    });
    if (!confirm.isConfirmed) return;

    this.setState({ loading: true });

    const jsonPart =
      jsonText === null
        ? ""
        : ", @credJson = '" + q(jsonText) + "', @jsonFileName = '" + q(jsonFileName) + "'";

    const res = await getDataTable(
      "EXEC dbo.NamshiCredAdmin_Save" +
      " @loginID = " + loginId +
      ", @brandID = " + parseInt(brandID, 10) +
      ", @country = '" + q(country) + "'" +
      ", @environment = '" + q(environment) + "'" +
      ", @projectName = '" + q(projectName) + "'" +
      ", @projectID = '" + q(projectID) + "'" +
      ", @credToken = '" + q(credToken.trim()) + "'" +
      ", @apiUrl = '" + q(apiUrl.trim()) + "'" +
      ", @portal = '" + q(this.state.portal || "NAMSHI") + "'" +
      jsonPart
    );

    this.setState({ loading: false });

    if (res === false) {
      Swal.fire("Not saved", "The credential could not be saved. Administrator access is required.", "error");
      return;
    }

    Swal.fire({
      icon: "success",
      title: this.state.editingCredID ? "Credential updated" : "Credential saved",
      timer: 1600, showConfirmButton: false,
    });
    this.resetEditor();
    await this.loadRows();
  };

  remove = async (r) => {
    const ok = await Swal.fire({
      title: "Delete this credential?",
      html:
        "<div style='text-align:left;font-size:13px'>" +
        "<b>" + (r.BrandName || "") + "</b> — " + r.Country + " / " + r.Environment + "<br/>" +
        "<b>Organisation:</b> " + (r.OrgName || "—") + "<br/><br/>" +
        "<span style='color:#cf1322'>Posting for this brand and country will stop working until a new credential is saved.</span></div>",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });
    if (!ok.isConfirmed) return;

    this.setState({ loading: true });
    await getDataTable(
      "EXEC dbo.NamshiCredAdmin_Delete @loginID = " + this.state.loginId + ", @credID = " + r.CredID
    );
    this.setState({ loading: false });
    await this.loadRows();
  };

  /* Decrypt and show the stored token. A deliberate, separate request — the
     value is never part of the browse response. */
  revealToken = async () => {
    if (!this.state.editingCredID) return;
    const dt = await getDataTable(
      "EXEC dbo.NamshiCredAdmin_RevealToken @loginID = " + this.state.loginId +
      ", @credID = " + this.state.editingCredID
    );
    const row = Array.isArray(dt) && dt.length ? dt[0] : null;
    if (!row || !row.CredToken) {
      Swal.fire({ icon: "info", title: "No token stored", timer: 1600, showConfirmButton: false });
      return;
    }
    this.setState({ credToken: row.CredToken });
  };

  viewJson = async (r) => {
    const dt = await getDataTable(
      "EXEC dbo.NamshiCredAdmin_GetJson @loginID = " + this.state.loginId + ", @credID = " + r.CredID
    );
    const row = Array.isArray(dt) && dt.length ? dt[0] : null;
    if (!row || !row.CredJson) {
      Swal.fire({ icon: "info", title: "No JSON stored for this credential", timer: 1800, showConfirmButton: false });
      return;
    }
    let pretty = row.CredJson;
    try { pretty = JSON.stringify(JSON.parse(row.CredJson), null, 2); } catch (e) {}
    Modal.info({
      title: (row.JsonFileName || "Credential JSON") + " — " + (r.OrgName || ""),
      width: Math.min(720, window.innerWidth * 0.92),
      okText: "CLOSE", closable: true, maskClosable: true,
      style: { top: 70, padding: 0 },
      bodyStyle: { padding: "14px 18px" },
      content: (
        <pre style={{ fontSize: 12, lineHeight: 1.5, background: "#f6f8fa", padding: 12, borderRadius: 6, margin: 0 }}>
          {pretty}
        </pre>
      ),
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  render() {
    const {
      loading, denied, orgs, brands, rows, filterOrg, search,
      brandID, country, environment, projectName, projectID,
      credToken, apiUrl, jsonFileName, editingCredID,
    } = this.state;

    if (denied) {
      return (
        <div className="content-wrapper">
          <section className="content">
            <div className="box box-danger">
              <div className="box-body" style={{ padding: 30, textAlign: "center" }}>
                <h4 style={{ color: "#cf1322", marginBottom: 8 }}>Administrator access required</h4>
                <p style={{ color: "#666", fontSize: 13 }}>
                  This screen manages Namshi credentials for every organisation.
                  Your account is not on the administrator list.
                </p>
              </div>
            </div>
          </section>
        </div>
      );
    }

    const lbl = { display: "block", marginBottom: 3 };
    const numBadge = {
      width: 28, height: 28, borderRadius: "50%", background: "#3D9970",
      color: "#fff", display: "flex", alignItems: "center",
      justifyContent: "center", fontWeight: 700, fontSize: 14, flexShrink: 0,
    };

    return (
      <div className="content-wrapper">
        {loading && (
          <div className="loader" style={{ display: "block" }}>
            <div className="loader-item"><Spin /></div>
          </div>
        )}

        <section className="content">
          <div className="box box-success">
            <div className="box-header with-border" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "12px 20px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "#aaa", marginBottom: 3 }}>
                  Dashboard &gt; Administration &gt; Portal Credentials (All Organisations)
                </div>
                <h3 className="box-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  Portal Credentials — Admin
                </h3>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#cf1322", border: "1px solid #ffa39e", background: "#fff1f0", padding: "3px 9px", borderRadius: 10 }}>
                  ALL ORGANISATIONS
                </span>
              </div>
            </div>

            <div className="box-body">

              {/* ── Editor ─────────────────────────────────────────── */}
              <div style={{ background: "#f8f9fa", border: "1px solid #e8e8e8", borderRadius: 8, padding: "16px 20px", marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <div style={numBadge}>1</div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                    {editingCredID ? "Update Credential" : "Add Credential"}
                  </div>
                  {editingCredID && (
                    <button className="btn btn-xs btn-default" style={{ marginLeft: 8 }} onClick={this.resetEditor}>
                      Cancel edit
                    </button>
                  )}
                </div>

                {/* Row 1 of the editor: the four classification fields, each
                    the same width so the row balances. Mirrors the user page. */}
                <div className="row">
                  <div className="col-xs-6 col-sm-3 col-md-3 margintop">
                    <label style={lbl}>Portal <span className="vcode">*</span></label>
                    <select className="form-control" value={this.state.portal}
                      onChange={(e) => {
                        const p = e.target.value;
                        this.setState({
                          portal: p,
                          apiUrl: p === "NAMSHI"
                            ? DEFAULT_URL[this.state.environment] || ""
                            : "",
                        });
                      }}>
                      {PORTALS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>

                  <div className="col-xs-12 col-sm-6 col-md-3 margintop">
                    <label style={lbl}>Brand <span className="vcode">*</span></label>
                    <select className="form-control" value={brandID}
                      onChange={(e) => this.setState({ brandID: e.target.value })}>
                      <option value="">Please Select Brand</option>
                      {brands.map((b) => (
                        <option key={b.DisplayId} value={b.DisplayId}>
                          {b.DisplayName} — {b.OrgName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-xs-6 col-sm-3 col-md-3 margintop">
                    <label style={lbl}>Country <span className="vcode">*</span></label>
                    {this.state.portal === "NAMSHI" ? (
                      <select className="form-control" value={country}
                        onChange={(e) => this.setState({ country: e.target.value })}>
                        {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      <input className="form-control" value={country} placeholder="AE, SA, UK, …"
                        onChange={(e) => this.setState({ country: e.target.value.toUpperCase() })} />
                    )}
                  </div>

                  <div className="col-xs-6 col-sm-3 col-md-3 margintop">
                    <label style={lbl}>Environment <span className="vcode">*</span></label>
                    <select className="form-control" value={environment}
                      onChange={(e) => this.onEnvChange(e.target.value)}>
                      {ENVIRONMENTS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                {/* Row 2 of the editor: project naming, side by side. */}
                <div className="row">
                  <div className="col-xs-12 col-sm-6 col-md-6 margintop">
                    <label style={lbl}>Project Name</label>
                    <input className="form-control" value={projectName} placeholder="Steve Madden_UAE"
                      onChange={(e) => this.setState({ projectName: e.target.value })} />
                  </div>

                  <div className="col-xs-12 col-sm-6 col-md-6 margintop">
                    <label style={lbl}>Project ID</label>
                    <input className="form-control" value={projectID} placeholder="PRJ460518"
                      onChange={(e) => this.setState({ projectID: e.target.value })} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-xs-12 col-sm-6 col-md-4 margintop">
                    <label style={lbl}>
                      Credential Token {!this.state.hasStoredToken && <span className="vcode">*</span>}
                    </label>
                    <div style={{ display: "flex", gap: 6 }}>
                      <input className="form-control" value={credToken} style={{ flex: 1 }}
                        placeholder={this.state.hasStoredToken
                          ? "Stored (" + (this.state.tokenMask || "hidden") + ") — leave blank to keep"
                          : (this.state.portal === "SHOPIFY"
                              ? "Shopify Admin API access token (shpat_…)"
                              : "Namshi cred value")}
                        onChange={(e) => this.setState({ credToken: e.target.value })} />
                      {this.state.hasStoredToken && (
                        <button className="btn btn-default" style={{ whiteSpace: "nowrap" }}
                          onClick={this.revealToken} title="Decrypt and show the stored token">
                          Reveal
                        </button>
                      )}
                    </div>
                    {this.state.hasStoredToken && (
                      <div style={{ fontSize: 11, color: "#888", marginTop: 3 }}>
                        Stored encrypted. Leave blank to keep it unchanged.
                      </div>
                    )}
                  </div>
                  <div className={"margintop " + (this.state.portal === "SHOPIFY" ? "col-xs-12 col-sm-6 col-md-8" : "col-xs-12 col-sm-6 col-md-4")}>
                    <label style={lbl}>{this.state.portal === "SHOPIFY" ? "Shop Domain" : "API URL"}</label>
                    <input className="form-control" value={apiUrl}
                      placeholder={this.state.portal === "SHOPIFY" ? "my-store.myshopify.com" : ""}
                      onChange={(e) => this.setState({ apiUrl: e.target.value })} />
                  </div>
                  {/* Shopify auth is the single access token above; the JSON
                      file column is a Namshi-only concept and is hidden rather
                      than shown unused for a Shopify row. */}
                  {this.state.portal !== "SHOPIFY" && (
                    <div className="col-xs-12 col-sm-6 col-md-4 margintop">
                      <label style={lbl}>Credential JSON File</label>
                      <input type="file" accept=".json,application/json" ref={this.fileRef}
                        className="form-control" style={{ paddingTop: 4 }} onChange={this.onPickJson} />
                      {jsonFileName ? (
                        <div style={{ fontSize: 11, color: "#3D9970", marginTop: 3 }}>
                          {this.state.jsonText === null ? "Stored file: " : "New file: "}{jsonFileName}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                  <button className="btn btn-success" onClick={this.save}>
                    {editingCredID ? "Update Credential" : "Save Credential"}
                  </button>
                </div>
              </div>

              {/* -- Filters + list -------------------------------------
                   Previously the title and the three filter controls shared
                   one flex row. On narrower content widths the row wrapped and
                   the controls stacked vertically against the right edge --
                   that is the misalignment the user flagged. Split in two:
                   the title alone on its own row, a proper filter bar below. */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 10px" }}>
                <div style={numBadge}>2</div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>All Saved Credentials</div>
              </div>

              {/* Filter bar: Portal + Organisation + Search, equal columns so
                  the three controls line up on the same baseline and nothing
                  wraps unless the viewport is genuinely tight. */}
              <div className="row" style={{ marginBottom: 14 }}>
                <div className="col-xs-12 col-sm-4 col-md-3 margintop">
                  <label style={lbl}>Portal</label>
                  <select className="form-control" value={this.state.filterPortal || ""}
                    onChange={(e) => this.setState({ filterPortal: e.target.value }, this.loadRows)}>
                    <option value="">All portals</option>
                    {PORTALS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="col-xs-12 col-sm-4 col-md-4 margintop">
                  <label style={lbl}>Organisation</label>
                  <select className="form-control" value={filterOrg}
                    onChange={(e) => this.setState({ filterOrg: e.target.value }, this.loadRows)}>
                    <option value="0">All organisations</option>
                    {orgs.map((o) => (
                      <option key={o.OrgID} value={o.OrgID}>
                        {o.OrgName} ({o.credCount})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-xs-12 col-sm-4 col-md-5 margintop">
                  <label style={lbl}>Search</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input className="form-control" style={{ flex: 1 }}
                      placeholder="Search brand / project…"
                      value={search}
                      onChange={(e) => this.setState({ search: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") this.loadRows(); }} />
                    <button className="btn btn-default" onClick={this.loadRows}>Search</button>
                    {/* Resets all three filter controls in one click rather than
                        clearing the text box and then still having to reselect
                        "All portals" / "All organisations" by hand. */}
                    {(filterOrg !== "0" || !!this.state.filterPortal || !!search) && (
                      <button className="btn btn-default" title="Clear all filters"
                        onClick={() => this.setState(
                          { filterOrg: "0", filterPortal: "", search: "" },
                          this.loadRows
                        )}>
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>SR NO.</th>
                      <th>PORTAL</th>
                      <th>ORGANISATION</th>
                      <th>BRAND</th>
                      <th>COUNTRY</th>
                      <th>ENVIRONMENT</th>
                      <th>PROJECT NAME</th>
                      <th>PROJECT ID</th>
                      <th>TOKEN</th>
                      <th>JSON</th>
                      <th>LAST UPDATED</th>
                      <th style={{ width: 130 }}>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan="12" style={{ textAlign: "center", color: "#999", padding: 18 }}>
                          No credentials match this filter.
                        </td>
                      </tr>
                    )}
                    {rows.map((r, i) => (
                      <tr key={r.CredID}>
                        <td>{i + 1}</td>
                        <td>
                          <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                            background: r.Portal === "SHOPIFY" ? "#f0f5ff" : "#fff7e6",
                            color:      r.Portal === "SHOPIFY" ? "#2f54eb" : "#d46b08" }}>
                            {r.Portal || "NAMSHI"}
                          </span>
                        </td>
                        <td style={{ fontSize: 12, fontWeight: 600 }}>{r.OrgName}</td>
                        <td><strong>{r.BrandName}</strong></td>
                        <td>
                          <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: r.Country === "UAE" ? "#e6f7ff" : "#f6ffed", color: r.Country === "UAE" ? "#1890ff" : "#389e0d" }}>
                            {r.Country}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: 11, fontWeight: 700, color: r.Environment === "PRODUCTION" ? "#cf1322" : "#888" }}>
                            {r.Environment}
                          </span>
                        </td>
                        <td>{r.ProjectName}</td>
                        <td>{r.ProjectID}</td>
                        <td style={{ fontFamily: "monospace", fontSize: 11, color: r.hasToken ? "#389e0d" : "#bbb" }}>
                          {r.hasToken ? r.CredTokenMask : "—"}
                        </td>
                        <td>
                          {r.hasJson
                            ? <button className="btn btn-xs btn-default" onClick={() => this.viewJson(r)}>View</button>
                            : <span style={{ color: "#bbb", fontSize: 11 }}>—</span>}
                        </td>
                        <td style={{ fontSize: 11, color: "#888" }}>
                          {r.UpdatedOn ? String(r.UpdatedOn).substring(0, 10) : ""}
                          {r.UpdatedByName ? " · " + r.UpdatedByName : ""}
                        </td>
                        <td>
                          <button className="btn btn-xs btn-default" style={{ marginRight: 4 }} onClick={() => this.editRow(r)}>Edit</button>
                          <button className="btn btn-xs btn-danger" onClick={() => this.remove(r)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }
}
